import { useCallback, useEffect, useState } from 'react';

import BannersBoxItem from '@/components/BannersBox/BannersBoxItem';
import { HANDLE_OPEN_CELEBRATION_MODAL_EVENT } from '@/components/Promotion/CelebrationBanner/helpers';
import UIResourceHtml from '@/components/UIResource/Html';
import { useUIResourcePool } from '@/ducks/common/resources';
import { exitCelebrationsReferredBookerFlowAction } from '@/ducks/programs/celebrations/actions';
import { selectCelebrationsIsFlowActive } from '@/ducks/programs/celebrations/selectors';
import { exitReferredBookerFlowAction } from '@/ducks/programs/mgm/actions';
import { getReferredBookerOptions } from '@/ducks/programs/mgm/selectors';
import { backToMainPage } from '@/ducks/voyageInfo/category/navigation';
import { clearBodyBackgroundClasses } from '@/helpers/util';
import useCmsValues from '@/hooks/useCmsValues';
import { useAppDispatch, useAppSelector } from '@/store';

import PgmXOutModal from '../CelebrationBanner/PgmXOutModal';

const MESSAGES = {
  CLEAR_CTA: 'celebrationsBanner.clearCTA',
} as const;

type Props = {
  className?: string;
  isClosable?: boolean;
  isDisabled?: boolean;
  onExitFlow?: () => void;
  onModalClose?: () => void;
};

const CelebrationBannerV2 = ({ className, isDisabled = false, onExitFlow = backToMainPage, onModalClose }: Props) => {
  const dispatch = useAppDispatch();

  const messages = useUIResourcePool(MESSAGES);

  const isCelebrationFlowActive = useAppSelector(selectCelebrationsIsFlowActive);
  const { program } = useAppSelector(getReferredBookerOptions);

  const [isShowPromotionBonusModal, setIsShowPromotionBonusModal] = useState(false);

  const [title, shortDescription] = useCmsValues(
    `${program}.highlightShareTitle`,
    `${program}.bookingBannerDescription`,
  );

  const openModal = useCallback(() => {
    setIsShowPromotionBonusModal(true);
  }, []);

  const closeModal = useCallback(
    (isExitReferredBookerFlow: boolean) => {
      if (isExitReferredBookerFlow) {
        onExitFlow?.();

        if (isCelebrationFlowActive) {
          dispatch(exitCelebrationsReferredBookerFlowAction());
        } else {
          dispatch(exitReferredBookerFlowAction());
        }

        backToMainPage({
          withCelebration: false,
          withChooseCabin: false,
          withChooseSailing: false,
          withFilters: false,
        });
      }
      onModalClose?.();

      setIsShowPromotionBonusModal(false);
      clearBodyBackgroundClasses('modal');
    },
    [dispatch, onModalClose, onExitFlow, isCelebrationFlowActive],
  );

  useEffect(() => {
    window.addEventListener(HANDLE_OPEN_CELEBRATION_MODAL_EVENT, openModal);
    return () => {
      window.removeEventListener(HANDLE_OPEN_CELEBRATION_MODAL_EVENT, openModal);
    };
  }, [openModal]);

  if (isDisabled || !isCelebrationFlowActive) {
    return null;
  }

  return (
    <>
      <BannersBoxItem
        className={className}
        clearCTALabel={messages.CLEAR_CTA}
        icon={<UIResourceHtml id="celebrationsBanner.Emoji" />}
        onClearClick={openModal}
        shortDescription={shortDescription}
        title={title}
      />
      <PgmXOutModal isOpen={isShowPromotionBonusModal} onCloseModal={closeModal} program={program} />
    </>
  );
};

export default CelebrationBannerV2;
